import React, { useState, useCallback, useEffect } from 'react'
import { IconButton, CircularProgress, Tooltip } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { CSVDownload } from 'react-csv'
import { httpsCallable } from 'firebase/functions'
import { firebase_functions } from '../../../config/firebaseConfig'
import { AutoTradeReportRow, CashFlowDetail } from '../../types/AutoTradeReport'

interface Trade {
  _id: string
  status: string
  reason?: string
  type: 'buy' | 'sell'
  symbol: string
  entryTime: number
  entryPrice: number
  exitTime?: number
  exitPrice?: number
  sl: number
  tp: number
  group: string
  lotSize: number
  openEquity: number
  commission: number
  profit?: number
  positionId?: number
  closeNotificationId?: string // Present when trade was closed via exit pattern
  errorSource?: string
  errorCode?: number
  errorMessage?: string
}

interface TradesDownloadButtonProps {
  row: AutoTradeReportRow
}

const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function formatDate(timestamp: number): string {
  if (!timestamp) return ''
  return new Date(timestamp).toLocaleString()
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-us', { style: 'currency', currency: 'USD' }).format(value)
}

function getTradeDuration(entryTime: number, exitTime?: number): string {
  if (!exitTime || !entryTime) return ''

  let diffSecs = Math.floor((exitTime - entryTime) / 1000)
  let diff = ''

  if (diffSecs >= 3600) {
    const hours = Math.floor(diffSecs / 3600)
    diff += `${hours}hr`
    diffSecs %= 3600
  }

  if (diffSecs >= 60) {
    const minutes = Math.floor(diffSecs / 60)
    diff += ` ${minutes}m`
    diffSecs %= 60
  }

  diff += ` ${diffSecs}s`
  return diff.trim()
}

// Calculate actual SL/TP price levels from distances - matches pattern-builder logic
function getStopLossTakeProfitLevels(trade: Trade): {
  slPrice: number | undefined
  tpPrice: number | undefined
} {
  if (trade.entryPrice === undefined || trade.sl === undefined || trade.tp === undefined) {
    return { slPrice: undefined, tpPrice: undefined }
  }

  // In our trade model, `sl` and `tp` represent the distance from entry, not the absolute price.
  const side = trade.type
  const slPrice = side === 'buy' ? trade.entryPrice - trade.sl : trade.entryPrice + trade.sl
  const tpPrice = side === 'buy' ? trade.entryPrice + trade.tp : trade.entryPrice - trade.tp
  return { slPrice, tpPrice }
}

// Check if exit price hit or crossed a level (SL or TP) - matches pattern-builder logic
function didHitOrCrossLevel(
  side: 'buy' | 'sell',
  kind: 'sl' | 'tp',
  exitPrice?: number,
  levelPrice?: number
): boolean {
  if (exitPrice === undefined || levelPrice === undefined) return false

  // Use epsilon tolerance for rounding/slippage
  const epsilon = Math.max(1e-4, Math.abs(levelPrice) * 1e-6)

  // BUY: SL is below entry (hit when exit <= slPrice), TP above (hit when exit >= tpPrice)
  // SELL: SL is above entry (hit when exit >= slPrice), TP below (hit when exit <= tpPrice)
  if (side === 'buy') {
    return kind === 'sl' ? exitPrice <= levelPrice + epsilon : exitPrice >= levelPrice - epsilon
  }
  return kind === 'sl' ? exitPrice >= levelPrice - epsilon : exitPrice <= levelPrice + epsilon
}

function determineExitType(trade: Trade): string {
  if (trade.status === 'REJECTED') {
    return trade.reason || 'REJECTED'
  }

  if (trade.status === 'PENDING') {
    return 'PENDING'
  }

  if (trade.status !== 'CLOSED' && trade.status !== 'closed') {
    return 'OPEN'
  }

  if (trade.exitPrice === undefined) return 'Unknown'

  // Priority 1: Check if closed by exit pattern (closeNotificationId exists)
  if (trade.closeNotificationId) {
    return 'Exit Pattern'
  }

  // Priority 2: Check if SL or TP was hit
  const { slPrice, tpPrice } = getStopLossTakeProfitLevels(trade)
  const side = trade.type
  const slHit = didHitOrCrossLevel(side, 'sl', trade.exitPrice, slPrice)
  const tpHit = didHitOrCrossLevel(side, 'tp', trade.exitPrice, tpPrice)

  if (tpHit) {
    return 'Take Profit'
  }
  if (slHit) {
    return 'Stop Loss'
  }

  // Priority 3: Default to manual
  return 'Manual'
}

export default function TradesDownloadButton({ row }: TradesDownloadButtonProps) {
  const [loading, setLoading] = useState(false)
  const [csvData, setCsvData] = useState<any[]>([])
  const [tradesDownloaded, setTradesDownloaded] = useState(false)

  const getBacktestTradesFunc = httpsCallable<
    { alertId: string; firstTradeDate?: string },
    { data: Trade[]; count: number; cashFlows?: CashFlowDetail[] }
  >(firebase_functions, 'getBacktestTrades')

  // Reset tradesDownloaded after download triggers
  useEffect(() => {
    if (tradesDownloaded) {
      setTradesDownloaded(false)
    }
  }, [tradesDownloaded])

  const formatTradesForCSV = useCallback(
    (trades: Trade[], cashFlows: CashFlowDetail[] = []) => {
      const data: any[] = []

      // Header section with alert info
      data.push(['Auto Trade Report - Individual Trades'])
      data.push(['Generated:', new Date().toLocaleString()])
      data.push([])
      data.push(['Alert Info'])
      data.push(['Alert Name:', row.alertName])
      data.push(['Pattern Name:', row.patternName])
      data.push(['User:', row.name])
      data.push(['Email:', row.userEmail])
      data.push(['GCID:', row.userGcid])
      data.push(['Trading House:', row.tradingHouse])
      data.push(['Server:', row.server || 'N/A'])
      data.push([])
      data.push(['Summary'])
      data.push(['Total Trades:', row.totalTrades])
      data.push(['Total Profit:', formatCurrency(row.totalProfit)])
      data.push([
        'Starting Equity:',
        row.startingEquity > 0 ? formatCurrency(row.startingEquity) : 'N/A',
      ])
      data.push(['Profit %:', row.startingEquity > 0 ? `${row.profitPercent.toFixed(2)}%` : 'N/A'])
      data.push(['Win Rate:', `${row.winRate}%`])
      data.push(['Winning Trades:', row.winningTrades])
      data.push(['Losing Trades:', row.losingTrades])
      data.push([])
      data.push([])

      // Trade columns header
      data.push([
        'Entry Date',
        'Day of Week',
        'Exit Date',
        'Duration',
        'Status',
        'Exit Type',
        'Type',
        'Symbol',
        'Entry Price',
        'Exit Price',
        'Stop Loss',
        'Take Profit',
        'Lot Size',
        'Open Equity',
        'Commission',
        'Profit',
        'Running P&L',
        'Position ID',
        'Error Source',
        'Error Code',
        'Error Message',
      ])

      // Build unified event list: trades keyed by entryTime, cash flows keyed by date
      type TradeEvent = { kind: 'trade'; time: number; trade: Trade }
      type CashFlowEvent = { kind: 'cashflow'; time: number; cf: CashFlowDetail }
      const events: (TradeEvent | CashFlowEvent)[] = [
        ...trades.map((t) => ({ kind: 'trade' as const, time: t.entryTime, trade: t })),
        ...cashFlows.map((cf) => ({
          kind: 'cashflow' as const,
          time: new Date(cf.date).getTime(),
          cf,
        })),
      ]
      events.sort((a, b) => a.time - b.time)

      let runningPL = 0
      let runningEquity = 0
      events.forEach((event) => {
        if (event.kind === 'trade') {
          const trade = event.trade
          const isClosed = trade.status === 'CLOSED' || trade.status === 'closed'
          if (isClosed && trade.profit) {
            runningPL += trade.profit
            runningEquity += trade.profit
          }
          if (trade.openEquity) {
            runningEquity = trade.openEquity
          }
          data.push([
            formatDate(trade.entryTime),
            trade.entryTime ? daysOfWeek[new Date(trade.entryTime).getDay()] : '',
            isClosed ? formatDate(trade.exitTime || 0) : 'Open Trade',
            isClosed ? getTradeDuration(trade.entryTime, trade.exitTime) : '',
            trade.status,
            determineExitType(trade),
            trade.type.toUpperCase(),
            trade.symbol,
            trade.entryPrice?.toFixed(5) || '',
            trade.exitPrice?.toFixed(5) || '',
            trade.sl?.toFixed(5) || '',
            trade.tp?.toFixed(5) || '',
            trade.lotSize?.toFixed(2) || '',
            trade.openEquity ? formatCurrency(trade.openEquity) : '',
            trade.commission ? formatCurrency(trade.commission) : '$0.00',
            isClosed && trade.profit !== undefined ? formatCurrency(trade.profit) : '',
            isClosed ? formatCurrency(runningPL) : '',
            trade.positionId || '',
            trade.errorSource || '',
            trade.errorCode || '',
            trade.errorMessage || '',
          ])
        } else {
          const cf = event.cf
          runningEquity += cf.amount
          data.push([
            formatDate(event.time),
            daysOfWeek[new Date(event.time).getDay()],
            '',
            '', // Exit Date, Duration
            cf.type,
            '',
            '',
            '', // Exit Type, Type, Symbol
            '',
            '',
            '',
            '',
            '', // Entry Price, Exit Price, SL, TP, Lot Size
            formatCurrency(runningEquity),
            '', // Commission
            formatCurrency(cf.amount),
            formatCurrency(runningPL),
            '',
            '',
            '',
            '', // Position ID, Error Source, Error Code, Error Message
          ])
        }
      })

      return data
    },
    [row]
  )

  const handleDownload = useCallback(async () => {
    if (loading || !row.alertId || row.totalTrades === 0) return

    setLoading(true)
    try {
      const result = await getBacktestTradesFunc({
        alertId: row.alertId,
        firstTradeDate: row.firstTradeDate !== 'Unknown' ? row.firstTradeDate : undefined,
      })
      const trades = result.data?.data || []
      const cashFlows = result.data?.cashFlows || []

      if (trades.length > 0) {
        const formattedData = formatTradesForCSV(trades, cashFlows)
        setCsvData(formattedData)
        setTradesDownloaded(true)
      }
    } catch (error) {
      console.error('Error fetching trades:', error)
    } finally {
      setLoading(false)
    }
  }, [row.alertId, row.totalTrades, loading, getBacktestTradesFunc, formatTradesForCSV])

  // Don't show button if no trades
  if (row.totalTrades === 0) {
    return null
  }

  return (
    <>
      {loading ? (
        <CircularProgress size={20} />
      ) : !tradesDownloaded ? (
        <Tooltip title="Download trades CSV">
          <IconButton size="small" onClick={handleDownload} color="primary">
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <CSVDownload data={csvData} target="_blank" />
      )}
    </>
  )
}
